import { EnvironmentProviders } from '@angular/core';
import { environment } from '@environment';
import { isEnvFeatureEnabled } from '@app/core/env-feature/env-feature';
import { LocalStorageService } from '@app/core/services/local-storage/local-storage.service';
import { MOCK_ENABLED_LS_KEY, MOCK_ENVS } from '@mock/mock.service';
import { MOCK_API_BASE_URL } from '@mock/msw/mock-api-base-url';

// our worker in front of MSW's generated `mockServiceWorker.js`, see the comment inside it
const WORKER_URL = `/app-mock-worker.js?api=${encodeURIComponent(MOCK_API_BASE_URL)}`;
const STOP_RELOADS_KEY = 'mock_worker_stop_reloads';
const MAX_STOP_RELOADS = 3;

// The same check as `MockService.isMockEnabled()`; it never passes on Production/OnPremise, which also replace this
// file with `start-mock-worker.prod.ts` (angular.json) and get no worker script.
export async function startMockWorker(): Promise<EnvironmentProviders[]> {
  const localStorageService = new LocalStorageService();
  const isEnabled =
    isEnvFeatureEnabled(MOCK_ENVS) && localStorageService.getFromLS<boolean>(MOCK_ENABLED_LS_KEY) === true;

  if (!isEnabled) {
    await stopMockWorker();
    return [];
  }

  // The source is chosen once per page load: the session talks to the mock API. The real one is asked only for
  // translations with "Mock for i18n" off, as before MSW (i18n.handlers.ts).
  const realApiBaseUrl = environment.apiBaseUrl;
  environment.apiBaseUrl = MOCK_API_BASE_URL;

  const { startWorker } = await import('./browser');

  try {
    return await startWorker(WORKER_URL, realApiBaseUrl);
  } catch (error) {
    // no fallback to the real backend: with mocks on, the app must not quietly talk to it
    const reason = error instanceof Error ? error.message : String(error);

    showStartError(reason, () => {
      localStorageService.saveToLS(MOCK_ENABLED_LS_KEY, false);
      location.reload();
    });
    throw new Error(`Mocks are on, but their Service Worker could not start: ${reason}`);
  }
}

function showStartError(reason: string, turnMocksOff: () => void): void {
  const toFixIt = /ssl|certificate/i.test(reason)
    ? [
        'To work with mocks:',
        '  1. Run "npm run cert:trusted" in the project folder - once per machine: it installs a certificate Chrome trusts for localhost.',
        '  2. Restart the dev server (and Chrome, if it still warns about the certificate).',
        '  3. Reload this page.',
        'Details: README.md, "Trusted certificate".',
      ]
    : ['To work with mocks: see the reason above and the browser console; README.md, "Mocks (MSW)".'];

  const text = document.createElement('pre');
  text.style.cssText = 'margin: 0 0 1rem; white-space: pre-wrap';
  text.textContent = [
    'Mocks are on, but their Service Worker could not start.',
    `Reason: ${reason}`,
    '',
    ...toFixIt,
    '',
    'To work without mocks:',
  ].join('\n');

  const button = document.createElement('button');
  // the app's global styles strip a button down to plain text
  button.style.cssText =
    'padding: 0.5rem 1rem; border: 1px solid currentColor; border-radius: 0.25rem; cursor: pointer';
  button.textContent = 'Turn mocks off and reload';
  button.addEventListener('click', turnMocksOff);

  const page = document.createElement('div');
  page.style.padding = '1rem';
  page.append(text, button);

  document.body.replaceChildren(page);
}

// any mock worker of this origin, not only one under the current WORKER_URL
const isMockWorker = (worker: ServiceWorker | null | undefined): boolean =>
  !!worker && /mock/i.test(new URL(worker.scriptURL).pathname);

// With mocks off no worker may stay. It is unregistered, and a page it still controls is loaded again: Chrome can hand
// the reload to the old worker while the leaving page holds it, so this may take a second round.
async function stopMockWorker(): Promise<void> {
  const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
  const mockRegistrations = registrations.filter(registration =>
    [registration.active, registration.waiting, registration.installing].some(isMockWorker)
  );

  await Promise.all(mockRegistrations.map(registration => registration.unregister()));

  const reloads = Number(sessionStorage.getItem(STOP_RELOADS_KEY) ?? 0);

  if (!isMockWorker(navigator.serviceWorker?.controller) || reloads >= MAX_STOP_RELOADS) {
    sessionStorage.removeItem(STOP_RELOADS_KEY);
    return;
  }

  sessionStorage.setItem(STOP_RELOADS_KEY, String(reloads + 1));
  location.reload();
  // the app must not bootstrap under the old worker while the reload is on its way
  await new Promise(() => undefined);
}
